import { App, TablePaginationConfig } from 'antd';
import { SorterResult } from 'antd/es/table/interface';

import { useGraphqlApiHeaders } from 'business/contextProviders/useApiHeaders';
import { SteeringTableFilterData } from 'business/steering/pages/table-page';
import { SteeringDataFields } from 'business/steering/pages/table-page/types';
import { useSteeringFilters } from 'business/steering/providers/steering-filters-provider';
import {
  Module_Enum,
  Parameter_Ring_Bool_Exp,
  useGetSteeringDataQuery,
} from 'generated/graphql';
import { FilterDataSetter } from 'technical/filter-data';
import { buildParameterRingDateOrFilter } from 'technical/graphql/parameter-ring-date-filter';
import { SORT_ORDER } from 'technical/graphql/sort-order';
import { validateRingRange } from 'technical/validation/rules';

export type SteeringTableParamsProps = Required<
  Pick<TablePaginationConfig, 'current' | 'pageSize'>
> &
  Pick<TablePaginationConfig, 'position'> & {
    sortField: SorterResult<SteeringDataFields>['field'];
    sortOrder: SorterResult<SteeringDataFields>['order'];
  };

export const useGetDataQuery = ({
  constructionSiteId,
  tableParams,
  setFilterData,
}: {
  constructionSiteId: string;
  tableParams?: SteeringTableParamsProps;
  setFilterData?: FilterDataSetter<SteeringTableFilterData>;
}) => {
  const { message } = App.useApp();
  const filters = useSteeringFilters();
  const headers = useGraphqlApiHeaders(Module_Enum.Steering);

  const combinedFilters: Parameter_Ring_Bool_Exp = {};

  if (
    filters.filterType === 'ring' &&
    filters.ringRange &&
    validateRingRange(filters.ringRange)
  ) {
    const [min, max] = filters.ringRange;
    combinedFilters.ringNumber = {
      _gte: min === null ? undefined : min,
      _lte: max === null ? undefined : max,
    };
  }

  if (filters.filterType === 'date' && filters.dateRange) {
    combinedFilters._or = buildParameterRingDateOrFilter(filters.dateRange);
  }

  let limit = undefined;
  let offset = undefined;
  let orderBy = undefined;

  if (tableParams) {
    limit = tableParams.pageSize;
    offset =
      tableParams.current && tableParams.pageSize
        ? (tableParams.current - 1) * tableParams.pageSize
        : undefined;

    orderBy =
      tableParams.sortField && tableParams.sortOrder
        ? {
            [tableParams.sortField.toString()]:
              SORT_ORDER[tableParams.sortOrder],
          }
        : undefined;
  }

  const { data, loading } = useGetSteeringDataQuery({
    variables: {
      constructionSiteId,
      limit,
      offset,
      filters: combinedFilters,
      orderBy,
    },
    context: {
      headers,
    },
    onCompleted: (res) => {
      setFilterData?.({
        selectedRings: res.parameter_ring_aggregate.aggregate?.count,
      });
    },
    onError: (error) => {
      message.open({
        type: 'error',
        content: error.message,
      });
    },
  });

  return { data, loading };
};
